import {
  HeroPlannerScheduleDebugIdentifier,
  HeroPlannerScheduleDebugOutcome,
  TodaysEscapesSchedule,
} from '~/queries/customerCommunication/requestGetTodaysEscapesScheduleDebug';
import { TodaysEscapesScheduleGenerationStatus } from '~/queries/customerCommunication/requestGetTodaysEscapesScheduleGenerationStatus';

import { LE_AGENT_HUB, LUXURY_ESCAPES } from '~/consts/brands';
import { DESTINATION_SEGMENT_ID, HOLIDAY_TYPE_SEGMENT_ID } from '~/consts/customerCommunication/heroPlanner';

import { getHeroPlannerGenerationSegmentName } from './heroPlannerGenerationUtils';

export const HERO_PLANNER_DEBUG_IDENTIFIER_FIELDS = [
  'countryGroup',
  'country',
  'state',
  'city',
  'segment',
  'loyalty',
] as const;

export type HeroPlannerDebugIdentifierField = (typeof HERO_PLANNER_DEBUG_IDENTIFIER_FIELDS)[number];

export type HeroPlannerDebugOfferSlot = 'hero' | 'tail' | 'push' | 'evening push' | 'custom';

export interface HeroPlannerDebugTarget extends HeroPlannerScheduleDebugIdentifier {
  key: string;
  countryGroupName: string;
  countryName: string;
  stateName: string;
  cityName: string;
  segmentName: string;
  loyaltyName: string;
}

type HeroPlannerRow = CustomerCommunication.HeroPlannerSchedulesListItem;

/**
 * - `skipped` rows are never sent, so they aren't requested (see `getHeroPlannerDebugSkipReason`)
 * - `heroOnly` rows have no tail deals schedule yet, but a hero planner row they'll be built from
 * - `regenerating` rows have a tail deals schedule that's still being (re)generated - or whose generation job died,
 *   since nothing moves it out of that state - so the send can't use it
 */
export type HeroPlannerDebugRowStatus = 'loading' | 'ok' | 'heroOnly' | 'regenerating' | 'failed' | 'skipped';

export interface HeroPlannerDebugRow {
  target: HeroPlannerDebugTarget;
  status: HeroPlannerDebugRowStatus;
  outcome?: HeroPlannerScheduleDebugOutcome;
  /** Identifier fields where the matched hero planner row differs from the requested one, i.e. it fell back */
  fallbackFields: Array<HeroPlannerDebugIdentifierField>;
  /** Where the searched offer appears in this schedule; empty when there is no search or no match */
  offerSlots: Array<HeroPlannerDebugOfferSlot>;
  /** Why the segment is never sent; only set on `skipped` rows */
  skipReason?: string;
  /**
   * The hero planner row the segment's schedule is built from: the fallback for `heroOnly` rows, or the row the
   * schedules endpoint matched for `ok` rows (when it's in the loaded list)
   */
  heroRow?: HeroPlannerRow;
  /** Why the hero planner fallback couldn't explain a failure */
  heroRowNote?: string;
}

export interface HeroPlannerDebugSummary {
  total: number;
  ok: number;
  heroOnly: number;
  regenerating: number;
  failed: number;
  loading: number;
  withOffer: number;
  /** Never-sent segments; not counted in `total` */
  skipped: number;
}

const DEFAULT_ID = 'DEFAULT';
const DEFAULT_COUNTRY_ID = DEFAULT_ID;
export const HERO_PLANNER_DEBUG_DEFAULT_LOYALTY_ID = DEFAULT_ID;
const EXPERIENCE_SEGMENT_ID = 'EXPERIENCE';
// the statuses svc-customer-communication `getTodaysEscapesScheduleForUserIdentifier` uses for a schedule that doesn't
// exist yet (404) and one whose tail deals aren't DONE (409); every other error is a real failure
const SCHEDULE_NOT_FOUND_STATUS = 404;
const SCHEDULE_REGENERATING_STATUS = 409;

/** Cities with an EXPERIENCE hero planner row on the send date, keyed by country group */
export type HeroPlannerDebugExperienceCities = Map<string, Set<string>>;

export function requiresHeroPlannerDebugExperienceCities(brand: string) {
  return brand !== LUXURY_ESCAPES;
}

/** Lifestyle EXPERIENCE segments can only be classified once the send date's experience cities are known */
export function isHeroPlannerDebugExperienceTarget(target: HeroPlannerScheduleDebugIdentifier, brand: string) {
  return requiresHeroPlannerDebugExperienceCities(brand) && target.segment === EXPERIENCE_SEGMENT_ID;
}

/** Hero planner rows for the send date, keyed by country group */
export type HeroPlannerDebugHeroRows = Map<string, Array<HeroPlannerRow>>;

export function getHeroPlannerDebugExperienceCities(heroRows: HeroPlannerDebugHeroRows) {
  const experienceCities: HeroPlannerDebugExperienceCities = new Map();
  heroRows.forEach((rows, countryGroupId) => {
    experienceCities.set(
      countryGroupId,
      new Set(rows.filter((row) => row.segmentId === EXPERIENCE_SEGMENT_ID).map((row) => row.cityId)),
    );
  });
  return experienceCities;
}

function hasTailDealsScheduleStatus(
  outcome: HeroPlannerScheduleDebugOutcome | undefined,
  brand: string,
  status: number,
) {
  // LeAgentHub only sends custom schedules, where these statuses mean "missing" and "not approved" instead
  return !!outcome && !outcome.ok && brand !== LE_AGENT_HUB && outcome.status === status;
}

/**
 * The schedules endpoint 404s until tail deals have been generated for the send date. For those we fall back to the
 * hero planner rows the schedule will be built from.
 */
export function isHeroPlannerDebugFallbackEligible(
  outcome: HeroPlannerScheduleDebugOutcome | undefined,
  brand: string,
) {
  return hasTailDealsScheduleStatus(outcome, brand, SCHEDULE_NOT_FOUND_STATUS);
}

/** The tail deals schedule exists but isn't DONE, so the send can't use it until generation finishes */
export function isHeroPlannerDebugRegenerating(outcome: HeroPlannerScheduleDebugOutcome | undefined, brand: string) {
  return hasTailDealsScheduleStatus(outcome, brand, SCHEDULE_REGENERATING_STATUS);
}

/** Rows that can't be sent as things stand */
export function isHeroPlannerDebugProblemRow(row: HeroPlannerDebugRow) {
  return row.status === 'failed' || row.status === 'regenerating';
}

// higher priority fields outweigh any combination of lower ones
const HERO_ROW_MATCH_WEIGHTS = { loyalty: 32, segment: 16, cadence: 8, state: 4, country: 2 } as const;

/**
 * Picks the hero planner row a segment's schedule will be built from. Mirrors svc-customer-communication tail deal
 * generation - `getHeroPlannerRowsForCountry` (validation.ts) for the candidates and `getBestMatchHeroPlannerRow`
 * (tailDealsUtil.ts) for the choice - so keep them in sync. Segments here are always DEFAULT cadence.
 *
 * `rows` must hold every hero planner row for the target's country group on the send date.
 */
export function findHeroPlannerDebugHeroRow(
  target: HeroPlannerScheduleDebugIdentifier,
  brand: string,
  rows: Array<HeroPlannerRow>,
): HeroPlannerRow | undefined {
  // custom schedules come back from the same list endpoint but live in their own table, so never match a hero row
  // lifestyle brands generate each segment separately, so only that segment's rows are candidates
  const scheduleRows = rows.filter(
    (row) =>
      row.segmentId !== DESTINATION_SEGMENT_ID &&
      row.segmentId !== HOLIDAY_TYPE_SEGMENT_ID &&
      (brand === LUXURY_ESCAPES || row.segmentId === target.segment),
  );

  const countryRows = scheduleRows.filter((row) => row.countryId === target.country);
  // with no rows for the country, the country group's DEFAULT country rows apply
  const candidates = countryRows.length > 0 ? countryRows : scheduleRows.filter((row) => row.countryId === DEFAULT_ID);

  if (target.segment === EXPERIENCE_SEGMENT_ID) {
    // the experience email needs a row for the exact city
    return candidates.find(
      (row) =>
        row.segmentId === EXPERIENCE_SEGMENT_ID &&
        row.countryId === target.country &&
        row.stateId === target.state &&
        row.cityId === target.city &&
        row.cadenceId === DEFAULT_ID &&
        row.membershipId === target.loyalty,
    );
  }

  const fields: Array<[keyof typeof HERO_ROW_MATCH_WEIGHTS, string, string]> = [
    ['loyalty', 'membershipId', target.loyalty],
    ['cadence', 'cadenceId', DEFAULT_ID],
    ['segment', 'segmentId', target.segment],
    ['state', 'stateId', target.state],
    ['country', 'countryId', target.country],
  ];

  let bestMatch: HeroPlannerRow | undefined;
  let highestScore = 0;

  candidates.forEach((row) => {
    let score = 0;
    for (const [weightKey, rowField, wanted] of fields) {
      const value = row[rowField as keyof HeroPlannerRow];
      if (value === wanted) {
        score += HERO_ROW_MATCH_WEIGHTS[weightKey];
      } else if (value !== DEFAULT_ID) {
        // a row targeting a different non-default value never applies
        return;
      }
    }
    // `>=` like the backend, so on a tie the later row wins
    if (score >= highestScore) {
      highestScore = score;
      bestMatch = row;
    }
  });

  return bestMatch;
}

/**
 * Why a segment is never sent, so a failure there doesn't matter; undefined when it is sent. Mirrors the identifier
 * filtering in svc-customer-communication `getScheduleGenerationStatus` (schedulesGenerationValidator.ts), the pre-send
 * check that decides which schedules must exist - keep the two in sync.
 *
 * `experienceCities` is only consulted for lifestyle EXPERIENCE segments; pass undefined while it's unknown and those
 * segments are treated as sent.
 */
export function getHeroPlannerDebugSkipReason(
  target: HeroPlannerScheduleDebugIdentifier,
  brand: string,
  experienceCities: HeroPlannerDebugExperienceCities | undefined,
): string | undefined {
  // LeAgentHub can have custom schedules for country = DEFAULT
  if (target.country === DEFAULT_COUNTRY_ID && brand !== LE_AGENT_HUB) {
    return 'Not sent: every subscriber has a country, so no schedule is generated for the DEFAULT country';
  }

  if (target.segment === DESTINATION_SEGMENT_ID || target.segment === HOLIDAY_TYPE_SEGMENT_ID) {
    return 'Not sent directly: subscribers with destinations or holiday types get the DEFAULT segment schedule with those attached';
  }

  if (
    isHeroPlannerDebugExperienceTarget(target, brand) &&
    experienceCities?.has(target.countryGroup) &&
    !experienceCities.get(target.countryGroup)?.has(target.city)
  ) {
    return 'Not sent: the experience email only goes to cities with an EXPERIENCE hero planner row on this date';
  }

  return undefined;
}

/** Hero planner rows for the send date can only be loaded by country group, or by country and state */
export interface HeroPlannerDebugRowLocation {
  country: string;
  state: string;
}

/**
 * Every country and state whose hero planner rows can apply to the country group's targets: the targets' own, plus the
 * DEFAULT state of each country and the DEFAULT country, which `findHeroPlannerDebugHeroRow` falls back to.
 */
export function getHeroPlannerDebugRowLocations(
  targets: Array<HeroPlannerScheduleDebugIdentifier>,
  countryGroupId: string,
): Array<HeroPlannerDebugRowLocation> {
  const locations = new Map<string, HeroPlannerDebugRowLocation>();
  const add = (country: string, state: string) => locations.set(`${country}|${state}`, { country, state });

  add(DEFAULT_COUNTRY_ID, DEFAULT_ID);
  targets
    .filter((target) => target.countryGroup === countryGroupId)
    .forEach((target) => {
      add(target.country, DEFAULT_ID);
      add(target.country, target.state);
    });

  return Array.from(locations.values());
}

export function getHeroPlannerDebugTargetKey(identifier: HeroPlannerScheduleDebugIdentifier) {
  return HERO_PLANNER_DEBUG_IDENTIFIER_FIELDS.map((field) => identifier[field]).join('|');
}

/**
 * Parses the backend's `heroPlannerScheduleIdentifier`, e.g.
 * `countryGroup:europe|country:DEFAULT|state:DEFAULT|city:DEFAULT|segment:DEFAULT|cadence:DEFAULT|loyalty:DEFAULT`.
 * Custom schedules append `|destination:BALI`.
 */
export function parseHeroPlannerScheduleIdentifier(identifier: string | undefined): Record<string, string> {
  if (!identifier) {
    return {};
  }

  return identifier.split('|').reduce<Record<string, string>>((parsed, part) => {
    const separatorIndex = part.indexOf(':');
    if (separatorIndex > 0) {
      parsed[part.slice(0, separatorIndex)] = part.slice(separatorIndex + 1);
    }
    return parsed;
  }, {});
}

export function isTailDealsSchedule(
  schedule: TodaysEscapesSchedule | undefined,
): schedule is Extract<TodaysEscapesSchedule, { emailHeroDeals: Array<string> }> {
  return !!schedule && 'emailHeroDeals' in schedule;
}

/**
 * The schedules endpoint resolves a requested identifier to the best matching hero planner row, falling back to
 * DEFAULT values when there is no exact row. Surfacing which fields fell back explains "why is this segment showing
 * that content".
 */
export function getHeroPlannerDebugFallbackFields(
  target: HeroPlannerScheduleDebugIdentifier,
  schedule: TodaysEscapesSchedule | undefined,
): Array<HeroPlannerDebugIdentifierField> {
  if (!schedule) {
    return [];
  }

  const matched = parseHeroPlannerScheduleIdentifier(schedule.heroPlannerScheduleIdentifier);

  return HERO_PLANNER_DEBUG_IDENTIFIER_FIELDS.filter(
    (field) => matched[field] !== undefined && matched[field].toLowerCase() !== target[field].toLowerCase(),
  );
}

/** Builds the backend's `heroPlannerScheduleIdentifier` for a hero planner row */
export function formatHeroPlannerScheduleIdentifier(heroRow: HeroPlannerRow) {
  const { countryGroupId, countryId, stateId, cityId, segmentId, cadenceId, membershipId } = heroRow;
  return `countryGroup:${countryGroupId}|country:${countryId}|state:${stateId}|city:${cityId}|segment:${segmentId}|cadence:${cadenceId}|loyalty:${membershipId}`;
}

/** The identifier of the row a segment resolved to: from the schedules endpoint, else from its hero planner row */
export function getHeroPlannerDebugMatchedIdentifier(row: HeroPlannerDebugRow): string | undefined {
  if (row.outcome?.ok) {
    return row.outcome.schedule?.heroPlannerScheduleIdentifier;
  }
  return row.heroRow ? formatHeroPlannerScheduleIdentifier(row.heroRow) : undefined;
}

/** Finds the hero planner row behind the schedules endpoint's `heroPlannerScheduleIdentifier` */
export function findHeroPlannerDebugHeroRowByIdentifier(
  identifier: string | undefined,
  rows: Array<HeroPlannerRow> | undefined,
): HeroPlannerRow | undefined {
  const parsed = parseHeroPlannerScheduleIdentifier(identifier);
  // custom schedules carry a destination/holiday type and live outside the hero planner rows
  if (!rows || !parsed.segment || parsed.destination !== undefined) {
    return undefined;
  }

  return rows.find(
    (row) =>
      row.countryGroupId === parsed.countryGroup &&
      row.countryId === parsed.country &&
      row.stateId === parsed.state &&
      row.cityId === parsed.city &&
      row.segmentId === parsed.segment &&
      row.cadenceId === parsed.cadence &&
      row.membershipId === parsed.loyalty,
  );
}

function getHeroRowFallbackFields(
  target: HeroPlannerScheduleDebugIdentifier,
  heroRow: HeroPlannerRow,
): Array<HeroPlannerDebugIdentifierField> {
  const rowValues: Record<HeroPlannerDebugIdentifierField, string> = {
    countryGroup: heroRow.countryGroupId,
    country: heroRow.countryId,
    state: heroRow.stateId,
    city: heroRow.cityId,
    segment: heroRow.segmentId,
    loyalty: heroRow.membershipId,
  };
  return HERO_PLANNER_DEBUG_IDENTIFIER_FIELDS.filter(
    (field) => (rowValues[field] ?? '').toLowerCase() !== target[field].toLowerCase(),
  );
}

/**
 * Custom (destination/holiday type) schedules carry offers in OFFER sections (`offerIds`), CUSTOM_OFFER sections
 * (`offerId`) and their pushes. Matches exact ids only, so a short id doesn't match a longer one or an image id.
 */
function findOfferInCustomSchedule(
  schedule: Exclude<TodaysEscapesSchedule, { emailHeroDeals: Array<string> }>,
  needle: string,
): Array<HeroPlannerDebugOfferSlot> {
  const slots: Array<HeroPlannerDebugOfferSlot> = [];
  const isInSections = (schedule.customSections ?? []).some(
    (section) =>
      ('offerIds' in section && section.offerIds?.includes(needle)) ||
      ('offerId' in section && section.offerId === needle),
  );
  if (isInSections) {
    slots.push('custom');
  }
  if (schedule.defaultPush?.offerId === needle) {
    slots.push('push');
  }
  if (schedule.eveningPush?.offerId === needle) {
    slots.push('evening push');
  }
  return slots;
}

export function findOfferInSchedule(
  schedule: TodaysEscapesSchedule | undefined,
  offerId: string,
): Array<HeroPlannerDebugOfferSlot> {
  const needle = offerId.trim();
  if (!schedule || !needle) {
    return [];
  }

  if (!isTailDealsSchedule(schedule)) {
    return findOfferInCustomSchedule(schedule, needle);
  }

  const slots: Array<HeroPlannerDebugOfferSlot> = [];
  if (schedule.emailHeroDeals?.includes(needle)) {
    slots.push('hero');
  }
  if (schedule.emailTailDeals?.includes(needle)) {
    slots.push('tail');
  }
  if (schedule.pushOfferId === needle) {
    slots.push('push');
  }
  if (schedule.eveningPush?.offerId === needle) {
    slots.push('evening push');
  }
  return slots;
}

interface BuildHeroPlannerDebugRowOptions {
  brand: string;
  outcome?: HeroPlannerScheduleDebugOutcome;
  offerId: string;
  skipReason?: string;
  /** Hero planner rows for the target's country group; undefined when they couldn't be (fully) loaded */
  heroRows?: Array<HeroPlannerRow>;
  isHeroRowsPending?: boolean;
}

export function buildHeroPlannerDebugRow(
  target: HeroPlannerDebugTarget,
  { brand, outcome, offerId, skipReason, heroRows, isHeroRowsPending }: BuildHeroPlannerDebugRowOptions,
): HeroPlannerDebugRow {
  if (skipReason) {
    return { target, status: 'skipped', skipReason, fallbackFields: [], offerSlots: [] };
  }

  if (!outcome) {
    return { target, status: 'loading', fallbackFields: [], offerSlots: [] };
  }

  if (isHeroPlannerDebugRegenerating(outcome, brand)) {
    // the hero planner row is only extra context here (what the schedule is built from), so don't wait for it
    const heroRow = heroRows ? findHeroPlannerDebugHeroRow(target, brand, heroRows) : undefined;
    return {
      target,
      outcome,
      heroRow,
      status: 'regenerating',
      fallbackFields: heroRow ? getHeroRowFallbackFields(target, heroRow) : [],
      offerSlots: [],
    };
  }

  if (isHeroPlannerDebugFallbackEligible(outcome, brand)) {
    if (isHeroRowsPending) {
      return { target, outcome, status: 'loading', fallbackFields: [], offerSlots: [] };
    }

    const heroRow = heroRows ? findHeroPlannerDebugHeroRow(target, brand, heroRows) : undefined;
    if (heroRow) {
      const needle = offerId.trim();
      return {
        target,
        outcome,
        heroRow,
        status: 'heroOnly',
        fallbackFields: getHeroRowFallbackFields(target, heroRow),
        offerSlots: needle && heroRow.emailHeroDeals?.includes(needle) ? ['hero'] : [],
      };
    }

    return {
      target,
      outcome,
      status: 'failed',
      fallbackFields: [],
      offerSlots: [],
      heroRowNote: heroRows
        ? 'No hero planner row matches this segment either'
        : "Couldn't load hero planner rows to check for a fallback",
    };
  }

  const schedule = outcome.ok ? outcome.schedule : undefined;

  return {
    target,
    outcome,
    heroRow: findHeroPlannerDebugHeroRowByIdentifier(schedule?.heroPlannerScheduleIdentifier, heroRows),
    status: outcome.ok ? 'ok' : 'failed',
    fallbackFields: getHeroPlannerDebugFallbackFields(target, schedule),
    offerSlots: findOfferInSchedule(schedule, offerId),
  };
}

export function summariseHeroPlannerDebugRows(rows: Array<HeroPlannerDebugRow>): HeroPlannerDebugSummary {
  return rows.reduce<HeroPlannerDebugSummary>(
    (summary, row) => ({
      total: summary.total + (row.status === 'skipped' ? 0 : 1),
      ok: summary.ok + (row.status === 'ok' ? 1 : 0),
      heroOnly: summary.heroOnly + (row.status === 'heroOnly' ? 1 : 0),
      regenerating: summary.regenerating + (row.status === 'regenerating' ? 1 : 0),
      failed: summary.failed + (row.status === 'failed' ? 1 : 0),
      loading: summary.loading + (row.status === 'loading' ? 1 : 0),
      withOffer: summary.withOffer + (row.offerSlots.length > 0 ? 1 : 0),
      skipped: summary.skipped + (row.status === 'skipped' ? 1 : 0),
    }),
    { total: 0, ok: 0, heroOnly: 0, regenerating: 0, failed: 0, loading: 0, withOffer: 0, skipped: 0 },
  );
}

const ROW_STATUS_ORDER: Record<HeroPlannerDebugRowStatus, number> = {
  failed: 0,
  regenerating: 1,
  loading: 2,
  heroOnly: 3,
  ok: 4,
  skipped: 5,
};

function isSameList(a: Array<string>, b: Array<string>) {
  return a.length === b.length && a.every((value, index) => value === b[index]);
}

/**
 * Rows are rebuilt on every render while the sweep streams in; this lets the table skip re-rendering the ones whose
 * result hasn't changed (outcomes and hero rows keep their identity thanks to react-query's structural sharing).
 */
export function isSameHeroPlannerDebugRow(a: HeroPlannerDebugRow, b: HeroPlannerDebugRow) {
  return (
    a === b ||
    (a.target === b.target &&
      a.status === b.status &&
      a.outcome === b.outcome &&
      a.heroRow === b.heroRow &&
      a.skipReason === b.skipReason &&
      a.heroRowNote === b.heroRowNote &&
      isSameList(a.fallbackFields, b.fallbackFields) &&
      isSameList(a.offerSlots, b.offerSlots))
  );
}

/** Failures first so they're visible without scrolling, never-sent last; otherwise keep the discovery order */
export function sortHeroPlannerDebugRows(rows: Array<HeroPlannerDebugRow>): Array<HeroPlannerDebugRow> {
  return rows
    .map((row, index) => ({ row, index }))
    .sort((a, b) => ROW_STATUS_ORDER[a.row.status] - ROW_STATUS_ORDER[b.row.status] || a.index - b.index)
    .map(({ row }) => row);
}

const PRE_SEND_CHECK_PASS_STATUS = 'PASS';

export function isHeroPlannerDebugPreSendCheckPassing(status: TodaysEscapesScheduleGenerationStatus | undefined) {
  return status?.status === PRE_SEND_CHECK_PASS_STATUS;
}

/** e.g. "Pre-send check", or "Travel pre-send check" for lifestyle brands, which are checked per segment */
export function getHeroPlannerDebugPreSendCheckName(segment: string | undefined) {
  if (!segment) {
    return 'Pre-send check';
  }
  return `${getHeroPlannerGenerationSegmentName(segment)} pre-send check`;
}
